/** Closed public transport checks, not policy approval or fingerprint issuance. */
import type {
  CommercialAcceptRequest, CommercialCommitmentOwnerPage, CommercialCommitmentOwnerView,
  CommercialOfferOwnerPage, CommercialOfferOwnerView,
} from "../types/generated";
import { InvalidApiResponseError } from "./errors";

const hash = (value: unknown): value is string => typeof value === "string" && /^[0-9a-f]{64}$/.test(value);
const uuid = (value: unknown): value is string => typeof value === "string"
  && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  && value !== "00000000-0000-0000-0000-000000000000";
const characters = (value: string): number => [...value].length;
const version = (value: unknown): value is string => typeof value === "string" && value.length > 0 && characters(value) <= 128;
const literal = (value: unknown, choices: readonly string[]): value is string => typeof value === "string" && choices.includes(value);
/** UUID locator spelling only; never case-fold actor IDs, hashes or source text. */
export const canonicalModuleOfferLocator = (value: unknown): string | null => uuid(value) ? value.toLowerCase() : null;
export const sameModuleOfferLocator = (left: unknown, right: unknown): boolean => uuid(left) && uuid(right)
  && left.toLowerCase() === right.toLowerCase();
const integer = (value: unknown, min = 0): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= min;
const timestamp = (value: unknown): value is string => typeof value === "string"
  && /T.+(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value));
function closed(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    && Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
}
const strings = (value: unknown, min = 0): value is string[] => Array.isArray(value)
  && value.length >= min && value.every(version);
const ids = (value: unknown): value is string[] => Array.isArray(value) && value.every(uuid);
const documentKeys = ["content", "content_sha256", "media_type", "template_key", "template_version",
  "rendering_version", "legal_party_reference", "jurisdiction_references", "language"];
function document(value: unknown, main: boolean): boolean {
  if (!closed(value, main ? [...documentKeys, "document_version_id", "created_at"] : documentKeys)) return false;
  return typeof value.content === "string" && value.content.length > 0 && hash(value.content_sha256)
    && literal(value.media_type, ["text/plain", "text/html"])
    && [value.template_key, value.template_version, value.rendering_version, value.legal_party_reference, value.language].every(version)
    && strings(value.jurisdiction_references, 1)
    && (!main || uuid(value.document_version_id) && timestamp(value.created_at));
}
function money(value: unknown): boolean {
  return closed(value, ["amount_minor", "currency"]) && integer(value.amount_minor)
    && literal(value.currency, ["aud", "cad", "eur", "usd"]);
}
function allowance(value: unknown): boolean {
  return closed(value, ["unit_key", "included_quantity", "maximum_quantity", "period", "over_limit_behavior"])
    && version(value.unit_key) && (value.included_quantity === null || integer(value.included_quantity))
    && (value.maximum_quantity === null || integer(value.maximum_quantity))
    && literal(value.period, ["billing_period", "contract", "not_applicable"])
    && literal(value.over_limit_behavior, ["block_new_operations", "not_applicable"]);
}
const economicKeys = ["cadence", "billable_unit", "unit_quantity", "contract_periods", "allowances", "trial_days",
  "grace_days", "renewal_policy_reference", "refund_policy_reference", "termination_policy_reference", "retention_policy_reference"];
function economics(value: unknown, v2: boolean): boolean {
  if (!closed(value, [...economicKeys, ...(v2 ? ["price_definition", "billing_period_total", "renewal_mode"] : ["software_price"])])) return false;
  if (!literal(value.cadence, ["monthly", "annual"]) || !version(value.billable_unit)
    || !integer(value.unit_quantity, 1) || !integer(value.trial_days) || !integer(value.grace_days)
    || !Array.isArray(value.allowances) || !value.allowances.every(allowance)
    || ![value.renewal_policy_reference, value.refund_policy_reference, value.termination_policy_reference, value.retention_policy_reference].every(version)) return false;
  if (!v2) return money(value.software_price) && integer(value.contract_periods, 1);
  return value.price_definition === "billing_period_total" && money(value.billing_period_total)
    && (value.renewal_mode === "fixed_term" && integer(value.contract_periods, 1)
      || value.renewal_mode === "auto_renew" && value.contract_periods === null);
}
const policyNames = ["renewal", "refund", "termination", "retention", "continuing_rights_policy"] as const;
function lifecycle(value: unknown): boolean {
  if (!closed(value, ["owner_edition_label", "definition_revision", "owner_terms_fingerprint", "cutoff_rules", "continuing_rights", ...policyNames])
    || !version(value.owner_edition_label) || !version(value.definition_revision) || !hash(value.owner_terms_fingerprint)
    || !closed(value.cutoff_rules, ["confirmed_cancellation", "fixed_term_completion", "failed_renewal"])
    || !Object.values(value.cutoff_rules).every(rule => rule === "verified_paid_through" || rule === "accepted_original_grace_end")
    || !closed(value.continuing_rights, ["policy_reference", "fulfilment", "history_finance_refunds", "cancellation_recovery", "privacy_safety", "retention"])) return false;
  for (const name of policyNames) {
    const policy = value[name];
    if (!closed(policy, ["reference", "revision", "document"]) || !version(policy.reference)
      || !version(policy.revision) || !document(policy.document, false)) return false;
  }
  const rights = value.continuing_rights, continuing = value.continuing_rights_policy;
  return closed(continuing, ["reference", "revision", "document"])
    && version(rights.policy_reference) && rights.policy_reference === continuing.reference
    && [rights.fulfilment, rights.history_finance_refunds, rights.cancellation_recovery, rights.privacy_safety]
      .every(rule => rule === "preserve_original_source_commitments")
    && rights.retention === "resolve_original_source_rules";
}
const offerKeys = ["terms_format", "offer_version_id", "economic_version_id", "module_key", "capability_version", "capability_keys",
  "audience_org_id", "title", "economics", "document", "fee_policy_version_ids", "effective_from", "effective_until",
  "published_at", "environment", "availability"];
function offer(value: unknown, orgId: string): value is CommercialOfferOwnerView {
  if (value === null || typeof value !== "object" || !("terms_format" in value)) return false;
  const v2 = value.terms_format === "software_terms_v2";
  if (!v2 && value.terms_format !== "legacy_fixed_v1") return false;
  const record: unknown = value;
  if (!closed(record, [...offerKeys, ...(v2 ? ["publication_format", "lifecycle_terms"] : ["purchase_enabled"])])
    || !uuid(record.offer_version_id) || !uuid(record.economic_version_id)
    || !literal(record.module_key, ["academy", "insights", "competitions"])
    || !version(record.capability_version) || !strings(record.capability_keys, 1)
    || record.audience_org_id !== null && !sameModuleOfferLocator(record.audience_org_id, orgId)
    || typeof record.title !== "string" || !record.title.length || characters(record.title) > 255
    || !economics(record.economics, v2) || !document(record.document, true) || !ids(record.fee_policy_version_ids)
    || !timestamp(record.effective_from) || record.effective_until !== null && !timestamp(record.effective_until)
    || !timestamp(record.published_at) || !literal(record.environment, ["sandbox", "production"])
    || !literal(record.availability, ["upcoming", "available", "expired", "unpublished"])) return false;
  if (!v2) return record.purchase_enabled === false;
  return record.publication_format === "historical_terms_only" && record.lifecycle_terms === null
    || record.publication_format === "module_policy_v1" && lifecycle(record.lifecycle_terms);
}
function commitment(value: unknown, orgId: string): value is CommercialCommitmentOwnerView {
  return closed(value, ["terms_format", "acceptance_id", "org_id", "offer", "accepted_at", "accepted_by_display_name", "licence_state"])
    && uuid(value.acceptance_id) && sameModuleOfferLocator(value.org_id, orgId) && offer(value.offer, orgId)
    && value.terms_format === value.offer.terms_format && timestamp(value.accepted_at)
    && (value.accepted_by_display_name === null || typeof value.accepted_by_display_name === "string")
    && value.licence_state === "not_activated";
}
function page(value: unknown): value is Record<string, unknown> & { items: unknown[]; total: number; limit: number; offset: number } {
  return closed(value, ["items", "total", "limit", "offset"]) && Array.isArray(value.items)
    && integer(value.total) && integer(value.limit, 1) && value.limit <= 100 && integer(value.offset)
    && value.items.length <= value.limit && value.items.length <= Math.max(0, value.total - value.offset);
}
function offerPage(value: unknown, orgId: string): value is CommercialOfferOwnerPage {
  return page(value) && value.items.every(item => offer(item, orgId))
    && new Set(value.items.map(item => offer(item, orgId) ? canonicalModuleOfferLocator(item.offer_version_id) : null)).size === value.items.length;
}
function commitmentPage(value: unknown, orgId: string): value is CommercialCommitmentOwnerPage {
  return page(value) && value.items.every(item => commitment(item, orgId))
    && new Set(value.items.map(item => commitment(item, orgId) ? canonicalModuleOfferLocator(item.acceptance_id) : null)).size === value.items.length;
}
export function isModuleOfferAcceptanceRequest(value: unknown): value is CommercialAcceptRequest {
  return closed(value, ["offer_version_id", "expected_document_sha256", "expected_owner_terms_fingerprint", "idempotency_key"])
    && uuid(value.offer_version_id) && uuid(value.idempotency_key)
    && hash(value.expected_document_sha256) && hash(value.expected_owner_terms_fingerprint);
}
export function requireAcceptance(value: unknown): asserts value is CommercialAcceptRequest {
  if (!isModuleOfferAcceptanceRequest(value)) {
    throw new TypeError("The exact reviewed acceptance facts are required.");
  }
}
export function readOffer(value: unknown, endpoint: string, orgId: string, versionId: string): CommercialOfferOwnerView {
  if (!offer(value, orgId) || !sameModuleOfferLocator(value.offer_version_id, versionId)) throw new InvalidApiResponseError(endpoint);
  return value;
}
export function readOfferPage(value: unknown, endpoint: string, orgId: string, expected: { limit: number; offset: number }): CommercialOfferOwnerPage {
  if (!offerPage(value, orgId) || value.limit !== expected.limit || value.offset !== expected.offset) throw new InvalidApiResponseError(endpoint);
  return value;
}
export function readCommitment(value: unknown, endpoint: string, orgId: string, acceptanceId: string): CommercialCommitmentOwnerView {
  if (!commitment(value, orgId) || !sameModuleOfferLocator(value.acceptance_id, acceptanceId)) throw new InvalidApiResponseError(endpoint);
  return value;
}
export function readCommitmentPage(value: unknown, endpoint: string, orgId: string, expected: { limit: number; offset: number }): CommercialCommitmentOwnerPage {
  if (!commitmentPage(value, orgId) || value.limit !== expected.limit || value.offset !== expected.offset) throw new InvalidApiResponseError(endpoint);
  return value;
}
export function readAccepted(value: unknown, endpoint: string, orgId: string, request: CommercialAcceptRequest): CommercialCommitmentOwnerView {
  if (!commitment(value, orgId) || value.offer.terms_format !== "software_terms_v2"
    || value.offer.publication_format !== "module_policy_v1" || value.offer.lifecycle_terms === null
    || !sameModuleOfferLocator(value.offer.offer_version_id, request.offer_version_id)
    || value.offer.document.content_sha256 !== request.expected_document_sha256
    || value.offer.lifecycle_terms.owner_terms_fingerprint !== request.expected_owner_terms_fingerprint) {
    throw new InvalidApiResponseError(endpoint);
  }
  return value;
}
